import {
  queryOptions,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { assistantAPI } from '../api/assistantAPI.ts';
import { FavoriteAssistantResponse } from '../../../../core/models/assistant/favoriteAssistantResponse.ts';

import { useTranslation } from 'react-i18next';
import { AssistantConfigurationModel } from '../../../../core/models/assistant/assistantConfigurationModel.ts';
import { NotificationService } from '@shared/components/toastNotification/NotificationService.tsx';
import { assistantMembershipKeys } from './assistantMembershipQueries.ts';
import { AssistantCompleteModel, AssistantDetailModel } from '../../types.ts';

//TO BE DONE : PROVIDE ERROR HANDLING FOR ALL QUERIES

/**
 * Query key factory for assistant-related queries
 * Following TkDodo's pattern: https://tkdodo.eu/blog/effective-react-query-keys
 * Structure: all → lists/details/logos → specific with params
 */
export const assistantKeys = {
  all: ['assistants'] as const,

  // Lists
  lists: () => [...assistantKeys.all, 'list'] as const,
  list: (type?: string | null, url?: string | null) =>
    [
      ...assistantKeys.lists(),
      { type: type ?? undefined, url: url ?? undefined },
    ] as const,

  // Details
  details: () => [...assistantKeys.all, 'detail'] as const,
  detail: (assistantName: string) =>
    [...assistantKeys.details(), assistantName] as const,

  // Logos
  logos: () => [...assistantKeys.all, 'logo'] as const,
  logo: (assistantName: string) =>
    [...assistantKeys.logos(), assistantName] as const,

  // Favorites
  favorites: () => [...assistantKeys.all, 'favorites'] as const,

  // Default model
  defaultModel: () => [...assistantKeys.all, 'defaultModel'] as const,

  // Form configuration
  formConfiguration: () => [...assistantKeys.all, 'formConfiguration'] as const,

  // Mutations
  mutations: {
    removeFavorite: () =>
      [...assistantKeys.all, 'mutation', 'removeFavorite'] as const,
    delete: () => [...assistantKeys.all, 'mutation', 'delete'] as const,
    upsert: () => [...assistantKeys.all, 'mutation', 'upsert'] as const,
  },
};

/** Builds the shared query options for listing assistants. */
export const getAssistantsQueryOptions = (type?: string, url?: string) =>
  queryOptions<AssistantCompleteModel[]>({
    queryKey: assistantKeys.list(type, url),
    queryFn: () => assistantAPI.listAssistantsAsync(type, url),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

export function useGetAssistants(type?: string, url?: string) {
  return useQuery(getAssistantsQueryOptions(type, url));
}

/** Builds the shared query options for resolving the default assistant model. */
export const getDefaultModelQueryOptions = () =>
  queryOptions({
    queryKey: assistantKeys.defaultModel(),
    queryFn: () => assistantAPI.getDefaultModelAsync(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

/** Builds the shared query options for an assistant logo. */
export const getAssistantLogoQueryOptions = (assistantName: string) =>
  queryOptions<Blob>({
    queryKey: assistantKeys.logo(assistantName),
    queryFn: () => assistantAPI.getAssistantLogo(assistantName),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    enabled: !!assistantName,
  });

export const useAssistantLogo = (assistantName: string) => {
  return useQuery(getAssistantLogoQueryOptions(assistantName));
};

/** Builds the shared query options for favorite assistants. */
export const getFavoriteAssistantsQueryOptions = () =>
  queryOptions<FavoriteAssistantResponse>({
    queryKey: assistantKeys.favorites(),
    queryFn: () => assistantAPI.getFavoriteAssistants(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

export const useGetFavoriteAssistants = () => {
  return useQuery(getFavoriteAssistantsQueryOptions());
};

/** Builds the shared query options for assistant details. */
export const getAssistantDetailsQueryOptions = (assistantName: string) =>
  queryOptions<AssistantDetailModel>({
    queryKey: assistantKeys.detail(assistantName),
    queryFn: async () => {
      if (!assistantName) {
        throw new Error('Assistant name is required');
      }
      return assistantAPI.getAssistantDetails(assistantName);
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: !!assistantName,
  });

export const useGetAssistantDetails = (
  assistantName: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    ...getAssistantDetailsQueryOptions(assistantName),
    enabled: !!assistantName && options?.enabled !== false,
  });
};

export const usePostFavoriteAssistant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assistantId: string) =>
      assistantAPI.postFavoriteAssistants(assistantId),
    // Optimistic update: update the cache before the mutation completes
    onMutate: async (assistantId: string) => {
      await queryClient.cancelQueries({ queryKey: assistantKeys.favorites() });
      const previousFavorites = queryClient.getQueryData(
        assistantKeys.favorites()
      );
      queryClient.setQueryData<FavoriteAssistantResponse | undefined>(
        assistantKeys.favorites(),
        (old) => ({
          ...old,
          favoriteAssistantIds: [
            ...(old?.favoriteAssistantIds ?? []),
            assistantId,
          ],
        })
      );
      return { previousFavorites };
    },
    // Rollback: restore the previous cache if the mutation fails
    onError: (_err, _assistantId, context) => {
      queryClient.setQueryData(
        assistantKeys.favorites(),
        context?.previousFavorites
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: assistantKeys.favorites() });
    },
  });
};

export const useDeleteFavoriteAssistant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: assistantKeys.mutations.removeFavorite(),
    mutationFn: (assistantId: string) =>
      assistantAPI.deleteFavoriteAssistants(assistantId),
    // Optimistic update: remove the assistantId from the cache before the mutation completes
    onMutate: async (assistantId: string) => {
      await queryClient.cancelQueries({ queryKey: assistantKeys.favorites() });
      const previousFavorites = queryClient.getQueryData(
        assistantKeys.favorites()
      );
      queryClient.setQueryData<FavoriteAssistantResponse | undefined>(
        assistantKeys.favorites(),
        (old) => ({
          ...old,
          favoriteAssistantIds: (old?.favoriteAssistantIds ?? []).filter(
            (id) => id !== assistantId
          ),
        })
      );
      return { previousFavorites };
    },
    // Rollback: restore the previous cache if the mutation fails
    onError: (_err, _assistantId, context) => {
      queryClient.setQueryData(
        assistantKeys.favorites(),
        context?.previousFavorites
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: assistantKeys.favorites() });
    },
  });
};

const removeAssistantByName = (
  assistants: AssistantCompleteModel[] | undefined,
  assistantName: string
): AssistantCompleteModel[] | undefined => {
  if (!Array.isArray(assistants)) {
    return assistants;
  }

  const filteredAssistants: AssistantCompleteModel[] = [];
  for (const assistant of assistants) {
    if (assistant.name !== assistantName) {
      filteredAssistants.push(assistant);
    }
  }

  return filteredAssistants;
};

/**
 * Custom hook for deleting an assistant
 * Uses React Query to handle loading, error states, and invalidation
 * Includes optimistic updates to immediately reflect changes in the UI
 * Handles snackbar notifications for success/error states
 */
export const useDeleteAssistant = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationKey: assistantKeys.mutations.delete(),
    mutationFn: (assistantName: string) =>
      assistantAPI.deleteAssistantAsync(assistantName),
    // Optimistic update: remove the assistant from the cache before the mutation completes
    onMutate: async (assistantName: string) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: assistantKeys.lists() });

      // Get all existing query cache entries for assistants (could be multiple with different URL params)
      const queryCache = queryClient.getQueryCache();
      const assistantQueries = queryCache.findAll({
        queryKey: assistantKeys.lists(),
      });

      // Store all previous states to restore in case of error
      const previousAssistantsData = assistantQueries.map((query) => ({
        queryKey: query.queryKey,
        data: queryClient.getQueryData(query.queryKey),
      }));

      // Update all assistant queries in the cache
      assistantQueries.forEach((query) => {
        queryClient.setQueryData(
          query.queryKey,
          (old: AssistantCompleteModel[] | undefined) => {
            if (Array.isArray(old)) {
              return removeAssistantByName(old, assistantName);
            }
            return old;
          }
        );
      });

      // Also update assistant details query if it exists
      const detailsQueryKey = assistantKeys.detail(assistantName);
      const previousDetails = queryClient.getQueryData(detailsQueryKey);
      if (previousDetails) {
        // We might want to just invalidate this query instead of removing it
        queryClient.removeQueries({ queryKey: detailsQueryKey });
      }

      // Return the snapshotted values
      return { previousAssistantsData, previousDetails, detailsQueryKey };
    },
    // Show success notification on successful completion
    onSuccess: (_data, assistantName) => {
      // Show success notification
      NotificationService.sendSuccess(
        t(
          'assistant.delete_success',
          `Assistant deleted successfully ${assistantName}`
        )
      );

      // Make sure to invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: assistantKeys.lists() });
    },
    // If the mutation fails, use the context we returned above and show error notification
    onError: (_err, assistantName, context) => {
      // Show error notification
      NotificationService.sendError(
        t(
          'assistant.delete_failed',
          `Failed to delete assistant ${assistantName}`
        )
      );

      // Restore all previous assistant lists
      if (context?.previousAssistantsData) {
        context.previousAssistantsData.forEach((item) => {
          queryClient.setQueryData(item.queryKey, item.data);
        });
      }

      // Restore assistant details if it existed
      if (context?.previousDetails && context?.detailsQueryKey) {
        queryClient.setQueryData(
          context.detailsQueryKey,
          context.previousDetails
        );
      }
    },
    // Always refetch after error or success to make sure the server state is reflected
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: assistantKeys.lists() });
    },
  });
};

/** Builds the shared query options for the personal assistant form configuration. */
export const getPersonalAssistantFormConfigurationQueryOptions = () =>
  queryOptions<AssistantConfigurationModel>({
    queryKey: assistantKeys.formConfiguration(),
    queryFn: () => assistantAPI.getAssistantCreationConfigurationAsync(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

/** Compatibility wrapper for incremental migration to query option factories. */
export const useGetPersonalAssistantFormConfiguration = () =>
  useQuery(getPersonalAssistantFormConfigurationQueryOptions());

/**
 * Interface for the file context exceeded error response from the backend
 * This error occurs when uploaded files exceed the model's token limit
 */
interface FileContextExceededErrorResponse {
  error_type: 'file_context_exceeded';
  message: string;
  current_tokens: number;
  max_tokens: number;
  model_name: string;
}

export const useUpsertPersonalAssistant = (upsertId?: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationKey: assistantKeys.mutations.upsert(),
    mutationFn: (data: FormData) => {
      return assistantAPI.createPersonalAssistantAsync(data);
    },
    onSuccess: async () => {
      if (upsertId) {
        await Promise.all([
          queryClient.refetchQueries({
            queryKey: assistantKeys.detail(upsertId),
          }),
          queryClient.refetchQueries({
            queryKey: assistantKeys.logo(upsertId),
          }),
          queryClient.invalidateQueries({
            queryKey: assistantMembershipKeys.detail(upsertId),
          }),
        ]);
      }

      await queryClient.refetchQueries({
        queryKey: assistantKeys.lists(),
      });

      NotificationService.sendSuccess(
        upsertId ? t('assistant.update_success') : t('assistant.create_success')
      );
    },
    onError: (error: unknown) => {
      // Check for 422 file_context_exceeded error
      const axiosError = error as {
        response?: {
          status?: number;
          data?: FileContextExceededErrorResponse;
        };
      };

      if (
        axiosError?.response?.status === 422 &&
        axiosError?.response?.data?.error_type === 'file_context_exceeded'
      ) {
        const { current_tokens, max_tokens, model_name } =
          axiosError.response.data;
        NotificationService.sendError(
          t('assistant.file_context_exceeded', {
            currentTokens: current_tokens.toLocaleString(),
            maxTokens: max_tokens.toLocaleString(),
            modelName: model_name,
          })
        );
      } else {
        // Show generic error notification
        NotificationService.sendError(t('assistant.create_failed'));
      }
    },
  });
};
